import React, { useId, useRef, useState } from "react";
import { caretCoordinates, type CaretPosition } from "./caretCoordinates";
import {
  findActiveMention,
  mentionGroups,
  textWithMention,
  type ActiveMention,
  type MentionCandidate,
  type MentionOption,
} from "./mentions";

/**
 * Typing "@" in the prompt offers the same people and processes as the pills;
 * picking one adds it to the scope and completes the visible mention, so the
 * prompt still says which person or process the author selected.
 */
export const useMentions = ({
  text,
  onTextChange,
  chosenUserIds = [],
  chosenProcessSlugs = [],
  onPick,
  userOptions,
  processOptions,
}: {
  text: string;
  onTextChange: (text: string) => void;
  userOptions: MentionOption[];
  processOptions: MentionOption[];
  chosenUserIds?: string[];
  chosenProcessSlugs?: string[];
  onPick: (candidate: MentionCandidate) => void;
}) => {
  const id = useId();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [mention, setMention] = useState<ActiveMention | null>(null);
  // Where the "@" itself sits, so the list opens against the token instead of
  // under the whole four-row textarea.
  const [anchor, setAnchor] = useState<CaretPosition | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const groups = mention
    ? mentionGroups(mention.query, userOptions, processOptions, chosenUserIds, chosenProcessSlugs)
    : [];
  const candidates = groups.flatMap(group => group.options);
  const open = anchor !== null && mention !== null;

  const close = () => {
    setMention(null);
    setAnchor(null);
  };

  /** Re-reads the token under the caret after anything that can move it. */
  const sync = (value: string, caret: number | null) => {
    const next = findActiveMention(value, caret ?? value.length);
    setMention(next);
    setAnchor(
      next && textareaRef.current ? caretCoordinates(textareaRef.current, next.start) : null,
    );
    setActiveIndex(0);
  };

  const pick = (candidate: MentionCandidate) => {
    if (!mention) return;
    const completedMention = textWithMention(text, mention, candidate);
    onTextChange(completedMention.text);
    onPick(candidate);
    close();
    // Put the caret immediately after the completed mention.
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(completedMention.caret, completedMention.caret);
    });
  };

  /** True when the list took the key, so the composer leaves it alone. */
  const handleKeyDown = (event: React.KeyboardEvent): boolean => {
    if (!open || event.nativeEvent.isComposing) return false;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      const step = event.key === "ArrowDown" ? 1 : candidates.length - 1;
      if (candidates.length) setActiveIndex((activeIndex + step) % candidates.length);
    } else if (event.key === "Enter" || event.key === "Tab") {
      if (candidates[activeIndex]) pick(candidates[activeIndex]);
      else if (event.key === "Tab") {
        close();
        return false;
      }
    } else if (event.key === "Escape") {
      close();
    } else {
      return false;
    }
    event.preventDefault();
    return true;
  };

  return {
    textareaRef,
    sync,
    close,
    handleKeyDown,
    menu:
      open && anchor
        ? {
            id,
            query: mention?.query ?? "",
            groups,
            candidates,
            anchor,
            activeIndex,
            onHover: setActiveIndex,
            onPick: pick,
          }
        : null,
  };
};
